import {
  useGoogleTagManager,
  useIsClient,
} from '@grace-studio/graceful-next/hooks';
import classNames from 'classnames';
import Icons from 'components/icons';
import ImageWrapper from 'components/ImageWrapper';
import Button from 'components/ui/Button';
import Text from 'components/ui/Text';
import VideoWrapper from 'components/VideoWrapper';
import { CSSProperties, FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUiState, setUi } from 'store/uiSlice';
import { Onboarding, OnboardingPlacement } from 'type';

type OnboardingViewerProps = {
  onboarding: Onboarding;
};

const OnboardingViewer: FC<OnboardingViewerProps> = ({ onboarding }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [elementRef, setElementRef] = useState<Element | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const isClient = useIsClient();
  const ui = useAppSelector(selectUiState) || {};
  const dispatch = useAppDispatch();
  const gtm = useGoogleTagManager();

  const current = useMemo(
    () => (currentStep >= 0 ? onboarding.steps[currentStep] : undefined),
    [currentStep, onboarding],
  );

  useEffect(() => {
    if (isClient) {
      const item = localStorage.getItem('onboarding');

      setShow(item !== 'done');
    }
  }, [isClient]);

  useEffect(() => {
    if (ui?.showOnboarding && isClient) {
      setShow(true);
      setCurrentStep(0);
    }
  }, [ui?.showOnboarding]);

  useEffect(() => {
    if (currentStep == onboarding.steps.length) {
      handleOnSkip();
    }
  }, [currentStep]);

  useEffect(() => {
    if (elementRef) {
      elementRef.classList.remove('onboarding-parent-active');
    }

    if (current && isClient && show) {
      document.querySelector('body')?.classList.add('onboarding-is-active');
      const eRef = document.querySelector(current.element);
      setElementRef(eRef);

      if (eRef) {
        eRef?.classList.add('onboarding-parent-active');
      }
    } else {
      setElementRef(null);
      document.querySelector('body')?.classList.remove('onboarding-is-active');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, isClient, show]);

  const elementRefPosition = useMemo(
    () => (elementRef ? elementRef.getBoundingClientRect() : null),
    [elementRef],
  );

  const getPlacementClasses = (placement: OnboardingPlacement) => {
    switch (placement) {
      case 'top':
        return 'pb-10 -translate-y-full -translate-x-1/2';
      case 'left':
        return 'pr-10 -translate-y-full -translate-x-full';
      case 'right':
      // case 'bottom':
      default:
        return 'pl-10 -translate-y-full';
    }
  };

  const getStyle = (placement: OnboardingPlacement): CSSProperties => {
    if (!elementRefPosition) {
      return {};
    }

    const position = elementRefPosition;
    const height = position.bottom - position.top;
    const width = position.right - position.left;

    switch (placement) {
      case 'top':
        return {
          top: position.top,
          left: position.left + width / 2,
        };
      case 'left':
        return {
          top: position.bottom + 48 - height / 2,
          left: position.left,
        };
      case 'right':
      // case 'bottom':
      default:
        return {
          top: position.bottom + 48 - height / 2,
          left: position.right,
        };
    }
  };

  const getArrowClasses = (placement: OnboardingPlacement) => {
    switch (placement) {
      case 'top':
        return 'bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2';
      case 'left':
        return 'bottom-12 right-0 translate-y-1/2 translate-x-1/2';
      case 'right':
      // case 'bottom':
      default:
        return 'bottom-12 left-0 translate-y-1/2 -translate-x-1/2';
    }
  };

  const handleOnSkip = () => {
    localStorage.setItem('onboarding', 'done');
    dispatch(
      setUi({
        ...ui,
        showOnboarding: false,
      }),
    );
    setShow(false);
  };

  return (
    current &&
    show &&
    elementRef && (
      <>
        {createPortal(
          <>
            {/* <div className="fixed inset-0 bg-red-400 opacity-50" /> */}
            <div className="overlay fixed z-10 top-0 left-0 h-screen w-screen bg-slate-500 opacity-60"></div>

            <div
              style={getStyle(current.placement)}
              className={classNames(
                getPlacementClasses(current.placement),
                'fixed transition-all duration-700 ',
                show ? 'onboarding-current z-[35]' : '',
              )}
            >
              <div className="bg-white rounded-lg w-onboarding relative ">
                <div className="rounded-t-lg overflow-hidden">
                  {current.image && <ImageWrapper image={current.image} />}
                  {current.media && <VideoWrapper media={current.media} />}
                  {current.localMedia && (
                    <VideoWrapper localMedia={current.localMedia} />
                  )}
                </div>
                <div className="p-8">
                  <Text
                    bold
                    color="grey-800"
                    elementType="div"
                    size="label"
                    className="small-caps mb-2"
                  >
                    {current.title}
                  </Text>
                  <Text
                    elementType="p"
                    className="mb-6 !text-sm !leading-[1.4]"
                  >
                    {current.text}
                  </Text>

                  <div className="flex items-center justify-between">
                    {onboarding.steps[currentStep + 1] ? (
                      <Button
                        style="onlyText"
                        className="!text-grey-500 hover:!text-black"
                        onClick={() => {
                          handleOnSkip();
                          gtm.trackEvent({
                            event: 'skip_onboarding',
                            eventInfo: {
                              name: current.title,
                            },
                          });
                        }}
                      >
                        {onboarding.buttons.skipText}
                      </Button>
                    ) : (
                      <div />
                    )}

                    {onboarding.steps[currentStep + 1] ? (
                      <Button
                        style="onlyText"
                        onClick={() => {
                          setCurrentStep(currentStep + 1);
                          gtm.trackEvent({
                            event: 'next_onboarding',
                            eventInfo: {
                              name: current.title,
                            },
                          });
                        }}
                      >
                        {onboarding.buttons.nextText}
                        <Icons.Arrow className="text-large w-[1em] ml-2" />
                      </Button>
                    ) : (
                      <Button
                        style="onlyText"
                        onClick={() => {
                          handleOnSkip();
                          gtm.trackEvent({
                            event: 'next_onboarding',
                            eventInfo: {
                              name: current.title,
                            },
                          });
                        }}
                      >
                        {onboarding.buttons.endText}
                        <Icons.Arrow className="text-large w-[1em] ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className={classNames(
                    'bg-white -z-[1] h-8 w-8 absolute rotate-45',
                    getArrowClasses(current.placement),
                  )}
                />
              </div>
            </div>
          </>,
          document.body,
        )}
      </>
    )
  );
};

export default OnboardingViewer;
